import {
  Route,
  RouteComponentProps,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { ReactElement, useEffect, useRef, useState } from 'react'

import { ConfiguratorRouteProps } from '../../config/routes'
import { ProfileContextProvider } from '../../context/ProfileContext'
import useLocale from '../../hooks/useLocale'
import { SpinnerContainer } from '../../pages/showroom/vehicle-list/VehicleList.styled'
import apiClient from '../../utils/api'
import useAuth from '../../utils/auth/useAuth'
import {
  businessStateKeys,
  getCurrentAppState,
  setAppState,
} from '../../utils/authAppState'
import { history } from '../../utils/historyStore'
import Spinner from '../spinner/Spinner'
import RebrandingLoader from '../utils/RebrandingLoader'

const setApiParameter = (
  parameterName: string,
  apiClientMethod: (p: string) => void,
) => {
  const ssParam = window.sessionStorage.getItem(parameterName)
  apiClientMethod(ssParam)
}

const PrivateRoute = ({
  component: Component,
  layout: Layout,
  profileNeeded,
  ...rest
}: ConfiguratorRouteProps): ReactElement => {
  const { isAuthenticated, isLoading, login } = useAuth()
  const [preparedLocation, setPreparedLocation] = useState<string | null>(null)
  const preparedEntry = useRef<string | null>(null)
  const location = useLocation()
  const locationKey = location.pathname + location.search + location.hash

  const locale = useLocale()

  const isOrderSummaryPage = useRouteMatch(
    '/:locale/order-summary/:quoteParam?',
  )?.isExact

  useEffect(() => {
    if (isLoading) return
    const search = new URLSearchParams(location.search)
    const entryKey = `${location.key}:${locationKey}`
    // Consume explicit entries before login; callbacks and renewal restore state
    // against the cleaned return URL, rather than starting another driver flow.
    if (preparedEntry.current !== entryKey) {
      setAppState(getCurrentAppState(), search.get('driverCase') === 'true')
      preparedEntry.current = entryKey
    }
    businessStateKeys.forEach((key) => search.delete(key))
    if (search.toString() !== location.search.replace(/^\?/, '')) {
      history.replace({
        search: search.toString(),
        hash: location.hash,
        state: history.location?.state,
      })
      return
    }
    if (!isAuthenticated) {
      login(getCurrentAppState({ returnTo: locationKey }))
      return
    }
    setApiParameter('caseId', apiClient.setCaseIdParameter)
    setApiParameter('renewalId', apiClient.setRenewalIdParameter)
    setApiParameter('opportunityId', apiClient.setOpportunityIdParameter)
    setApiParameter('policyLevel', apiClient.setPolicyLevelParameter)
    setApiParameter('customerId', apiClient.setCustomerIdParameter)
    setApiParameter('draftPolicy', apiClient.setDraftPolicyParameter)
    setApiParameter(
      'submittedQuoteNumber',
      apiClient.setSubmittedQuoteNumberParameter,
    )
    setPreparedLocation(locationKey)
  }, [
    isAuthenticated,
    isLoading,
    login,
    locationKey,
    location.key,
    location.search,
    location.hash,
  ])

  const authenticationSet = isAuthenticated && preparedLocation === locationKey

  return isLoading || !authenticationSet ? (
    <SpinnerContainer>
      <Spinner size={20} />
    </SpinnerContainer>
  ) : (
    <Route
      render={(props: RouteComponentProps) =>
        authenticationSet ? (
          <ProfileContextProvider
            locale={locale}
            profileNeeded={profileNeeded}
            isOrderSummaryPage={isOrderSummaryPage}
          >
            <RebrandingLoader />
            <Layout>
              <Component {...props} />
            </Layout>
          </ProfileContextProvider>
        ) : null
      }
      {...rest}
    />
  )
}

export default PrivateRoute
